import React from 'react';
import { FileX, WifiOff, Search, Database, AlertCircle, RefreshCw } from 'lucide-react';
import notFoundImage from './not-found.png';
import './NotFoundPage.scss';

/**
 * EmptyState component - used for displaying empty states when:
 * - No data found from server
 * - Server error / no response
 * - Search returned no results
 * 
 * @param {string} variant - 'empty' | 'error' | 'search' | 'offline'
 * @param {string} title - Main title text
 * @param {string} message - Optional description message
 * @param {function} onRetry - Optional retry callback (shows retry button if provided)
 * @param {string} retryText - Custom text for retry button
 * @param {boolean} showImage - Whether to show the image (default: true)
 * @param {string} size - 'small' | 'medium' | 'large' (default: 'medium')
 */
const EmptyState = ({ 
  variant = 'empty',
  title,
  message,
  onRetry,
  retryText = 'Try Again',
  showImage = true,
  size = 'medium'
}) => {
  // Default titles based on variant
  const getDefaultTitle = () => {
    switch (variant) {
      case 'error':
        return 'Something went wrong';
      case 'search':
        return 'No results found';
      case 'offline':
        return 'Connection lost';
      case 'empty':
      default:
        return 'No data available';
    }
  };

  // Default messages based on variant
  const getDefaultMessage = () => {
    switch (variant) {
      case 'error':
        return 'Unable to load data. Please try again.';
      case 'search':
        return 'Try adjusting your search or filters';
      case 'offline':
        return 'Please check your internet connection';
      case 'empty':
      default:
        return null;
    }
  };

  // Icons based on variant (used when image fails or in small mode)
  const getIcon = () => {
    const iconProps = { 
      className: 'empty-state__icon',
      strokeWidth: 1.5
    };

    switch (variant) {
      case 'error':
        return <AlertCircle {...iconProps} />;
      case 'search':
        return <Search {...iconProps} />;
      case 'offline':
        return <WifiOff {...iconProps} />;
      case 'empty':
      default:
        return <FileX {...iconProps} />;
    }
  };

  const displayTitle = title || getDefaultTitle();
  const displayMessage = message !== undefined ? message : getDefaultMessage();

  return (
    <div className={`empty-state empty-state--${variant} empty-state--${size}`}>
      <div className="empty-state__content">
        {showImage && size !== 'small' ? (
          <div className="empty-state__image">
            <img 
              src={notFoundImage} 
              alt="Empty State"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="empty-state__icon-wrapper" style={{ display: 'none' }}>
              {getIcon()}
            </div>
          </div>
        ) : (
          <div className="empty-state__icon-wrapper">
            {getIcon()}
          </div>
        )}
        
        <div className="empty-state__text">
          <h3 className="empty-state__title">{displayTitle}</h3>
          {displayMessage && (
            <p className="empty-state__message">{displayMessage}</p>
          )}
        </div>

        {onRetry && (
          <div className="empty-state__actions">
            <button 
              className="empty-state__retry-btn"
              onClick={onRetry}
              type="button"
            >
              <RefreshCw className="empty-state__retry-icon" />
              {retryText}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Keep backward compatibility with old NotFound component
export const NotFound = ({ title }) => (
  <EmptyState variant="search" title={title} />
);

export default EmptyState;
